import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { ActionBarSortTool } from '../../app/content/actionBar/ActionBarSortTool';
import { DataConnectionsGrid } from '../../app/content/DataConnectionsGrid';
import { DatasourceGrid } from '../../app/content/DatasourceGrid';
import { ProjectGrid } from '../../app/content/ProjectGrid';
import { SiteGrid } from '../../app/content/SiteGrid';
import { ViewGrid } from '../../app/content/ViewGrid';
import { WorkbookGrid } from '../../app/content/WorkbookGrid';
import { IPlaceScope } from '../../app/IPlaceScope';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { SorterActions } from '../../common/react/SorterActions';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { SelectionEvent } from '../directives/tbSelectionMenu';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { ListResult } from '../fetchers/ListResult';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext } from '../helpers/ActionContext';
import { Order } from '../helpers/Order';
import { SubplaceCountsHelper, SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { L10n } from '../L10n/L10n';
import { ContentFiltersKeys } from '../models/ContentFilters';
import {
  DataConnectionContentType,
  DatasourceContentType,
  IContentType,
  ProjectContentType,
  SitesContentType,
  ViewContentType,
  WorkbookContentType,
} from '../models/ContentTypes';
import { FlowLayout } from '../models/FlowLayout';
import { IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { IPlace, ISubplace } from '../models/IPlace';
import { IProjectDetailInfo } from '../models/IProjectInfo';
import { ISliceable } from '../models/ISliceable';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookInfo } from '../models/IWorkbookInfo';
import {
  IFavoriteItem,
  IIdItemInfo,
  IItemStatisticsInfo,
  INamedItemInfo
} from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataConnections } from '../resources/DataConnections';
import { Favorites } from '../resources/Favorites';
import { IActionResource, IDetailResource } from '../resources/Resource';
import { ResourceQuery } from '../resources/ResourceQuery';
import { IProjectIdResult } from '../ServerApi/requests/CreateProjectRequest';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { INamedItem } from '../ServerApi/types/resourceParts';
import { ActionPanelService, IActionButton, IListActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ContentActions } from '../services/ContentActions';
import { ContentUrl } from '../services/ContentUrl';
import { CreateSiteAction } from '../services/CreateSiteAction';
import { DeprecatedAnalyticsService } from '../services/DeprecatedAnalyticsService';
import { EffectivePermissionsService } from '../services/EffectivePermissionsService';
import { FilterPanelService } from '../services/FilterPanelService';
import { HelpService } from '../services/HelpService';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import { Sorter, SorterFactory } from '../services/SorterFactory';
import { ToasterService } from '../services/ToasterService';
import { WindowLocationService } from '../services/WindowLocationService';
import { IDataSourcePlaceScope } from './DatasourcePlaceCtrl';
import { FilterPanelTypeaheadScope, IFilterPanelContainerScope } from './FilterPanelCtrl';
import { IProjectPlaceScope } from './ProjectPlaceCtrl';
import { SwitchableContentScope } from './SwitchableContentWithUrlParamCtrl';

/**
 * These are type "any" because they are specified in the state config as Angular factory functions,
 * which can take many forms (either a function or an array containing injected dependencies).
 * Their actual types are specified in the constructor parameters to ContentSubplaceCtrl.
 */
export interface IContentSubplaceResolve {
  subplace: any;
  shouldFetchDetails: any;
}

export interface ListActionsScope {
  listActions: IListActionButton[];
  availableListAction: (listAction: IListActionButton) => boolean;
}

export interface SelectedItemsScope<T> {
  selectedItems: Set<T>;
}

export interface SelectedDetailedItemScope<TItem, TDetailItem> {
  detailedItem: TDetailItem;
  getItemDetails: (item: TItem) => ng.IPromise<TDetailItem>;
}

export interface SortToolPropsScope {
  sortToolProps: ActionBarSortTool.Props;
}

export interface SubplaceWithDataGridScope<T>
  extends SelectedItemsScope<T> {
  dataGridProps: DataGrid.Props;
  sorter: Sorter;

  // the following properties are used only for Angular data grids and Angular thumbnail views.
  // Delete these scope properties once the data grids and thumbnail views are all in React. TFS: 609897
  emptyTemplateModel: IEmptyTemplateModel;
  sliceable: ISliceable<T>;
  sliceChanged: (newSlice: ListResult<T>) => any;
}

export interface ThumbnailViewScope<TItem> {
  cardTestId(item: TItem, itemType: string): string;
  detailsUrl(item: TItem, subplaceName?: string): string;
  gridLayout: (width: number, height: number) => FlowLayout;
  isEmpty: boolean;
  isTouch: boolean;
  showModeSwitcher(): boolean;
}

export interface IContentSubplaceScope
  extends IPlaceScope,
  IActionsMenuScope<any, any>,
  CommonDataGridColumns.IFavoriteColumnScope<IFavoriteItem & IItemStatisticsInfo<any>>,
  IFilterPanelContainerScope,
  FilterPanelTypeaheadScope,
  ListActionsScope,
  RefreshContentScope,
  SelectedDetailedItemScope<any, any>,
  SortToolPropsScope,
  SubplaceWithActionButtonsScope<any, any>,
  SubplaceWithDataGridScope<any>,
  SwitchableContentScope,
  ThumbnailViewScope<any> {

  contentType: string;
  showActionBarRight(): boolean;
  busy: boolean;

  // For project thumbnails only
  shouldShowReadMore: ($event: JQueryEventObject) => boolean;

  // For workbook and datasource thumbnails only
  hitCountType: string;
  hitCountCaption_translated: string;

  // template workbooks
  createWorkbookFromTemplate(): void;

  // Used for force update on actions menu
  onAllowedActionsUpdate: (object: any) => void;

  // expose this method so we can localize directly in the pug files
  localizeItemCount: (stringKey: string, input: number) => string;

}

export class ContentSubplaceCtrl {

  public static $inject = [
    // Services
    '$interval',
    '$location',
    '$scope',
    '$q',
    '$translate',
    '$state',
    '$sanitize',
    'AngularToReactDataGridBridge',
    'BrowserTitleService',
    'ContentActions',
    'CreateSiteAction',
    'QueryService',
    'FilterPanelService',
    'ActionPanelService',
    'ServerService',
    'SorterFactory',
    'ContentUrl',
    'Favorites',
    'DataConnections',
    'HelpService',
    'AngularContextFactory',
    'DeprecatedAnalyticsService',
    'WindowLocationService',
    'ToasterService',
    'EffectivePermissionsService',

    // Resolved on ui-router state config, keep in sync with IContentSubplaceResolve
    'subplace',
    'shouldFetchDetails',
  ];

  private query: IQueryUpdater;

  constructor(
    $interval: ng.IIntervalService,
    private $location: ng.ILocationService,
    private $scope: IContentSubplaceScope,
    $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private $state: ng.ui.IStateService,
    private $sanitize: ng.sanitize.ISanitizeService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    BrowserTitleService: BrowserTitleService,
    private ContentActions: ContentActions,
    private CreateSiteAction: CreateSiteAction,
    queryService: QueryService,
    filterPanelService: FilterPanelService,
    private actionPanelService: ActionPanelService,
    private ServerService: ServerService,
    SorterFactory: SorterFactory,
    ContentUrl: ContentUrl,
    Favorites: Favorites,
    private dataConnections: DataConnections,
    private HelpService: HelpService,
    private AngularContextFactory: AngularContextFactory,
    DeprecatedAnalyticsService: DeprecatedAnalyticsService,
    WindowLocationService: WindowLocationService,
    ToasterService: ToasterService,
    private EffectivePermissionsService: EffectivePermissionsService,
    private subplace: ISubplace<any>,
    shouldFetchDetails: (item: INamedItemInfo) => boolean
  ) {

    $scope.localizeItemCount = (stringKey: string, count: number) =>
      $translate.instant(stringKey, { COUNT: count || 0 }, L10n.Interpolations.Count);

    // Sort Order
    $scope.sorter = SorterFactory.create(subplace.type.defaultOrder);
    $scope.$on('$destroy', () => $scope.sorter.detach());

    $scope.selectedItems = new Set<INamedItem>((item: INamedItem) => item.id);
    this.configureForReactDataGrid(subplace.type, $scope.place);

    $scope.busy = false;
    BrowserTitleService.setTitleWithPlaceEntityNameAndSubplaceName({placeEntityName: $scope.objectName, translatableSubplaceName: subplace.name});

    $scope.isTouch = UserAgentSupport.isTouch();

    $scope.contentType = subplace.type.name;

    // Filter panel controls
    $scope.filterControls = {};

    const explicitFilterKeys = subplace.filterKeys();
    $scope.filterDefinitions = filterPanelService.getFilterDefinitions(explicitFilterKeys);

    $scope.filterFieldValues = (filterField: string) => {
      return (matchText: string) => subplace.filterValuesQuery(filterField).withMatchText(matchText);
    };

    $scope.getFieldValueQuery = (filterField: string) => {
      return subplace.filterValuesQuery(filterField).withMatchText('');
    };

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems, $scope.detailedItem, $scope.allowedActions),
      menuActions: this.actionsFor(subplace.type, $scope.place),
      getActionTarget: this.getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    // The Angular actions menu is still used in the project/workbook/view thumbnails
    ActionButtonsHelper.setupScopeForAngularActionsMenu($scope, this.actionsFor(subplace.type, $scope.place));
    $scope.availableAction = (action: IActionButton<any, any>) => action.isAvailable($scope.selectedItems, $scope.detailedItem, $scope.allowedActions);

    this.setListActionsOnScope();

    // Layout
    $scope.gridLayout = <(width: number, height: number) => FlowLayout>
      _.once((width: number, height: number) => {
        return new FlowLayout(width, height);
      });

    // Popovers
    $scope.getItemDetails = (item: INamedItemInfo) => {
      const detailsResource = <IDetailResource<any>><any>this.subplace.resource;
      return detailsResource.getById(item.id);
    };

    this.configureSubplaceToFetchAndSetDetailedItemIfOnlyOneItemIsSelectedOrSomething($scope, $q, shouldFetchDetails);

    $scope.refreshContent = () => {
      this.refresh();
      $scope.$emit(SubplaceEvents.RefreshCounts, subplace.name);
    };

    $scope.$on(SubplaceEvents.Refresh, () => {
      $scope.refreshContent();
    });

    $scope.$on(SelectionEvent.SelectionStarted, () => {
      $scope.busy = true;
    });

    $scope.$on(SelectionEvent.SelectionEnded, () => {
      $scope.busy = false;
    });

    this.query = queryService.createQueryUpdaterFromSubplace(
      $scope,
      subplace,
      (query: ResourceQuery<any>) => {
        $scope.isEmpty = undefined; // Before the query returns data, we don't know if the scope is empty. Leaving it true or false may cause a flicker
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: $scope, sliceable: query });
        $scope.sliceable = query;
        $scope.sorter.setOrder(query.getOrder());
      });

    $scope.cardTestId = (item: IIdItemInfo, itemType: string) => `${itemType}-${item.id}-card`;

    $scope.detailsUrl = (item: any, subplaceName?: string) => ContentUrl.forItem(item, subplace.type, subplaceName);

    $scope.sliceChanged = this.createSliceChangedFunctionForScope($scope, subplace);

    $scope.anyActionsAllowed = () => this.anyActionsAllowed(subplace.type.name, $scope.place, $scope.ownerId);

    $scope.showModeSwitcher = this.createShowModeSwitcherFunctionForScope($scope);

    $scope.showActionBarRight = this.createShowActionBarRightFunctionForScope($scope);

    $scope.shouldShowReadMore = this.createShouldShowReadMoreFunctionForScope();

    $scope.addFavorite = this.createAddFavoritesFunctionForScope($scope, Favorites);

    $scope.removeFavorite = this.createRemoveFavoritesFunctionForScope($scope, Favorites);

    $scope.onAllowedActionsUpdate = angular.noop;

    // Used whenever datagrid is empty (there were no items, or user searched for non-existent item, or non-admin)
    $scope.emptyTemplateModel = this.createEmptyTemplateModel($scope.contentType, $scope.place);

    this.refresh();
  }

  private setListActionsOnScope() {

    // Hacky type assertions to determine if we're in the project or data source place
    const projectPlaceScope = this.$scope.$parent as IProjectPlaceScope;
    const datasourcePlaceScope = this.$scope.$parent as IDataSourcePlaceScope;
    const project: IProjectDetailInfo | undefined = projectPlaceScope.project;
    const datasource: IDataSourceDetailInfo | undefined = datasourcePlaceScope.datasource;

    this.$scope.listActions = this.createListActions(project, datasource);
    this.$scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    const canCreateProject = project ? projectPlaceScope.allowedAction('createProject') : null;

    this.$scope.listActions = [
      ...this.createListActions(project, datasource),
      ...this.createProjectSubplaceListActions(project, canCreateProject)
    ];
  }

  private createListActions(project: IProjectDetailInfo | undefined, datasource: IDataSourceDetailInfo | undefined) {
    return [
      {
        name: 'create-new-site',
        translate: 'newSite',
        isAvailable: () => this.subplace.type.name === SitesContentType.name && this.ServerService.isUISiteCreationEnabled(), //Tableau Online sites must be created w/ Zeus
        execute: ($event: JQueryEventObject) => {
          this.CreateSiteAction.execute(this.$scope).then(() => this.$state.hardReload());
        }
      },
      {
        name: 'create-workbook-from-scratch',
        translate: 'newWorkbook',
        isAvailable: () => this.subplace.type.name === WorkbookContentType.name && FeatureFlags.webCreateNewWorkbookEnabled && this.ServerService.canSessionUserAuthorOnWeb(),
        execute: ($event: JQueryEventObject) => {
          let siteName = this.ServerService.sessionSiteUrlName();
          let projectName = project ? project.id.toString() : '';
          if (!datasource || this.$scope.place.name !== 'datasource') {
            this.ContentActions.newEmptyWorkbook(siteName, projectName);
          } else {
            this.ContentActions.newWorkbookFromDatasource(datasource, siteName);
          }
        }
      }
    ];
  }

  private createProjectSubplaceListActions(project: IProjectDetailInfo | undefined, hasProjectLeaderCapability: boolean) {
    return [
      {
        name: 'create-project',
        translate: 'createProject',
        isAvailable: () => this.subplace.type.name === ProjectContentType.name &&
          (this.ServerService.isSessionSiteOrServerAdmin() || hasProjectLeaderCapability || this.userOwnsProject(project)),
        execute: ($event: JQueryEventObject) => {
          this.ContentActions.createProject(ActionContext.fromEvent($event), project ? project.id : null)
            .then((result: IProjectIdResult) => {
              if (result.id) {
                this.$scope.refreshContent();
              }
            });
        }
      }
    ];
  }

  private userOwnsProject(project: IProjectDetailInfo) {
    return project ? this.ServerService.sessionUserId() === project.owner.id : false;
  }

  private configureSubplaceToFetchAndSetDetailedItemIfOnlyOneItemIsSelectedOrSomething($scope: IContentSubplaceScope, $q: ng.IQService, shouldFetchDetails: (item: INamedItemInfo) => boolean) {

    //TODO: Remove this check once all objects have permissions
    const shouldGetActions = (<IActionResource<any>><any>this.subplace.resource).actions != null;
    const getActions = (item: INamedItemInfo) => {
      const actionResource = <IActionResource<any>><any>this.subplace.resource;
      // Embedded data sources don't have their own permissions, so we skip calling getDatasourceActions on them
      if (!shouldGetActions || ($scope.contentType === DatasourceContentType.name && (<IDataSourceInfo>item).workbook)) {
        return $q.when({});
      }
      return actionResource.actions(item.id);
    };

    // Fetch and set detailed item iff 1 item is selected

    $scope.detailedItem = null;
    let pendingDetailId: string;

    $scope.$watch(
      () => ($scope.selectedItems.size() === 1) ? $scope.selectedItems.first().id : null,
      (id: string) => {
        $scope.detailedItem = null;
        $scope.allowedActions = null;

        if (_.isString(id)) {
          pendingDetailId = id;
          const item = $scope.selectedItems.first();
          const fetchDetails = shouldFetchDetails(item) ? $q.resolve($scope.getItemDetails(item)) : angular.noop;
          const fetchActions = shouldGetActions ? getActions(item) : angular.noop;
          $q.all([
            fetchDetails,
            fetchActions
          ])
            .then((values: any) => {
              $scope.detailedItem = values[0];
              $scope.allowedActions = values[1];
              $scope.onAllowedActionsUpdate($scope);
            }
            );
        } else {
          pendingDetailId = null;
        }
      }
    );
  }

  private createSliceChangedFunctionForScope($scope: IContentSubplaceScope, subplace: ISubplace<any>) {
    return (newSlice: ListResult<any>) => {
      $scope.hitCountType = this.getHitCountType();
      $scope.hitCountCaption_translated = this.getHitCountDescription($scope.hitCountType);
      $scope.isEmpty = newSlice.total === 0;

      $scope.selectedItems.updateItems(newSlice.items);
      SubplaceCountsHelper.updateCountsFromSubplace($scope, subplace, newSlice.total);
    };
  }

  private createShowModeSwitcherFunctionForScope($scope: IContentSubplaceScope) {
    return () => {
      switch ($scope.contentType) {
        case ProjectContentType.name:
        case WorkbookContentType.name:
        case ViewContentType.name:
          return true;
        default:
          return false;
      }
    };
  }

  private createShowActionBarRightFunctionForScope($scope: IContentSubplaceScope) {
    // There isn't a sort tool for the sites grid yet (TFS: 615695), so we need to hide it
    return () => $scope.contentType !== SitesContentType.name;
  }

  private createShouldShowReadMoreFunctionForScope() {
    return ($event: JQueryEventObject): boolean => {
      const card = $($event.currentTarget).parent();
      const descriptionContainerHeight = card.find('.tb-card-view-description').height() - 16; // 16px == lineHeight
      const descriptionTextHeight = card.find('.tb-card-view-description-text').height();
      return descriptionTextHeight > descriptionContainerHeight;
    };
  }

  private createAddFavoritesFunctionForScope($scope: IContentSubplaceScope, Favorites: Favorites) {
    return (item: IFavoriteItem & IItemStatisticsInfo<any>) => {
      item.usageInfo.favoritesTotal++;
      switch ($scope.contentType) {
        case DatasourceContentType.name:
          return Favorites.addDatasource(<IDataSourceInfo>item);
        case WorkbookContentType.name:
          return Favorites.addWorkbook(<IWorkbookInfo>item);
        case ViewContentType.name:
          return Favorites.addView(<IViewInfo>item);
        default:
          return;
      }
    };
  }

  private createRemoveFavoritesFunctionForScope($scope: IContentSubplaceScope, Favorites: Favorites) {
    return (item: IFavoriteItem & IItemStatisticsInfo<any>) => {
      item.usageInfo.favoritesTotal--;
      switch ($scope.contentType) {
        case DatasourceContentType.name:
          return Favorites.removeDatasource(<IDataSourceInfo>item);
        case WorkbookContentType.name:
          return Favorites.removeWorkbook(<IWorkbookInfo>item);
        case ViewContentType.name:
          return Favorites.removeView(<IViewInfo>item);
        default:
          return;
      }
    };
  }

  private configureForReactDataGrid(contentType: IContentType, place: IPlace) {
    const sorterActions: SorterActions = {
      replaceOrderClause: (field: string, newField: string, newDirection: string) => this.$scope.$apply(() =>
        this.$scope.sorter.removeFieldAndSetPrimary(field, newField, newDirection)),
      setOrder: (order: IOrderClause[]) => this.$scope.$apply(() =>
        this.$scope.sorter.setOrder(order))
    };
    const columns = this.reactDataGridColumnsFor(contentType, place, sorterActions);
    const emptyTemplateModel = this.createEmptyTemplateModel(contentType.name, place);

    this.$scope.sortToolProps = {
      sorterActions: sorterActions,
      translate: this.$translate.instant.bind(this.$translate),
      contentType: contentType.name,
      placeName: place ? place.name : null,
      searchFilter: this.$location.search()[ContentFiltersKeys.search]
    };

    // TODO: Add a store of some type so that this watch can be removed.
    this.$scope.$watch(
      () => this.$location.search()[ContentFiltersKeys.search],
      (searchFilter: string) => this.$scope.sortToolProps.searchFilter = searchFilter);

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(emptyTemplateModel),
      canShowFilterPanel: contentType.canShowFilterPanel,
    });

    this.$scope.dataGridProps.updated = (rows: DataGrid.Row[]) => {
      this.$scope.$apply(() => {
        this.$scope.isEmpty = rows.length === 0;
        this.$scope.selectedItems.updateItems(_.map(_.compact(rows), (row) => row.data));
        SubplaceCountsHelper.updateCountsFromSubplace(this.$scope, this.subplace, rows.length);
      });
    };


    this.$scope.$watch(() => this.$scope.sorter.selectedColumnKey() + ';' + this.$scope.sorter.isSortAscending(),
      () => {
        this.$scope.dataGridProps.cols = this.reactDataGridColumnsFor(contentType, place, sorterActions);
      });
  }

  private actionsFor(contentType: IContentType, place: IPlace): IActionButton<any, any>[] {
    switch (contentType.name) {
      case ProjectContentType.name:
        return this.actionPanelService.getProjectActions();
      case SitesContentType.name:
        return this.actionPanelService.getSitesActions();
      case WorkbookContentType.name:
        return this.actionPanelService.getWorkbookActions();
      case ViewContentType.name:
        return this.actionPanelService.getViewActions();
      case DatasourceContentType.name:
        return this.actionPanelService.getDatasourceActions(place);
      case DataConnectionContentType.name:
        return this.actionPanelService.getDataConnectionActions();
    }
  }

  private getActionTarget = (): IActionTarget<any, any> => {
    return {
      items: this.$scope.selectedItems,
      detailedItem: this.$scope.detailedItem,
      actionContext: { scope: this.$scope },
      allowedActions: this.$scope.allowedActions
    };
  }

  private reactDataGridColumnsFor(contentType: IContentType,
    place: IPlace,
    sorterActions: SorterActions) {

    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    switch (contentType.name) {

      case ProjectContentType.name:
        return ProjectGrid.columns(
          this.$sanitize,
          this.$translate.instant.bind(this.$translate),
          siteUrls,
          this.actionsFor(contentType, place),
          this.getActionTarget,
          this.$scope,
          this.anyActionsAllowed(contentType.name, place, this.$scope.ownerId));

      case SitesContentType.name:
        return SiteGrid.columns(
          this.$translate.instant.bind(this.$translate),
          this.$scope,
          this.actionsFor(contentType, place),
          this.getActionTarget,
          siteUrls,
          this.ServerService);

      case WorkbookContentType.name:
        return WorkbookGrid.columns(
          this.$translate.instant.bind(this.$translate),
          this.$scope,
          this.AngularContextFactory.create(this.$scope),
          siteUrls,
          this.actionsFor(contentType, place),
          this.getActionTarget,
          this.anyActionsAllowed(contentType.name, place, this.$scope.ownerId),
          place.name === 'project',
          sorterActions,
          this.$location.search.bind(this.$location));

      case ViewContentType.name:
        return ViewGrid.columns(
          this.$translate.instant.bind(this.$translate),
          this.$scope,
          siteUrls,
          this.actionsFor(contentType, place),
          this.getActionTarget,
          this.anyActionsAllowed(contentType.name, place, this.$scope.ownerId),
          place.name === 'project',
          place.name === 'workbook',
          sorterActions,
          this.$location.search.bind(this.$location));

      case DatasourceContentType.name:
        return DatasourceGrid.columns(
          this.$translate.instant.bind(this.$translate),
          this.$scope,
          siteUrls,
          this.actionsFor(contentType, place),
          this.getActionTarget,
          this.anyActionsAllowed(contentType.name, place, this.$scope.ownerId),
          place.name === 'project',
          place.name === 'workbook',
          this.AngularContextFactory.create(this.$scope),
          this.dataConnections,
          sorterActions,
          this.$location.search.bind(this.$location));

      case DataConnectionContentType.name:
        return DataConnectionsGrid.columns(
          this.$translate.instant.bind(this.$translate),
          this.$scope,
          siteUrls,
          this.actionsFor(contentType, place),
          this.getActionTarget,
          this.anyActionsAllowed(contentType.name, place, this.$scope.ownerId),
          place.name === 'datasource',
          place.name === 'workbook');

      default:
        throw 'No react data grid for content type ' + contentType.name;
    }
  }

  // Is the user able to perform any actions
  private anyActionsAllowed(contentType: string, place: IPlace, ownerId: string) {
    switch (contentType) {

      case ProjectContentType.name:
        return this.ServerService.isSessionSiteOrServerAdmin();

      case DataConnectionContentType.name:
      case DatasourceContentType.name:
        if (place.name === 'workbook') {
          // Viewing a data sources subplace in a workbook place and the user is not an admin or the workbook owner
          return this.ServerService.isSessionSiteOrServerAdmin() || ownerId === this.ServerService.sessionUserId();
        }

      default:
        return true;
    }
  }

  private refresh() {
    this.query.refresh();
  }

  private createEmptyTemplateModel(contentType: string, place: IPlace) {
    const emptyTemplateModel = <IEmptyTemplateModel>{};

    switch (contentType) {
      case ViewContentType.name:
        emptyTemplateModel.title = 'viewsEmptyMessage';
        break;
      case WorkbookContentType.name:
        const isDataSourcePlace = place.name === 'datasource';
        emptyTemplateModel.title = isDataSourcePlace ? 'workbooksForDataSourceEmptyMessage' : 'workbooksEmptyMessage';
        break;
      case ProjectContentType.name:
        emptyTemplateModel.title = 'projectsEmptyMessage';
        break;
      case DataConnectionContentType.name:
        emptyTemplateModel.title = 'connectionsEmptyMessage';
        this.populateDatasourceHelpUrl(emptyTemplateModel);
        break;
      case DatasourceContentType.name:
        emptyTemplateModel.title = 'datasourcesEmptyMessage';
        this.populateDatasourceHelpUrl(emptyTemplateModel);
        break;
      case SitesContentType.name:
        emptyTemplateModel.title = 'sitesEmptyMessage';
        break;
    }
    return emptyTemplateModel;
  }

  private populateDatasourceHelpUrl(template: IEmptyTemplateModel) {
    const helpUrl = this.HelpService.getDatasourceHelpUrl();
    if (helpUrl) {
      template.help = {
        links: [{
          localizedValueKey: 'remoteRefreshSchedulesEmptyHelp',
          url: helpUrl
        }]
      };
    }
  }

  private defaultHitCountType = 'hitsTotal';
  private getHitCountType() {
    const relevantFields = ['hitsLastOneMonthTotal', 'hitsLastThreeMonthsTotal', 'hitsLastTwelveMonthsTotal'];
    let hitCountField: string;

    const query = this.$location.search();
    if (query && query.order) {
      const orderClauses = Order.fromInput(query.order);
      hitCountField = Order.startsWithRelevantField(orderClauses, relevantFields);
    }

    return hitCountField || this.defaultHitCountType;
  }

  private getHitCountDescription(hitCountType: string) {
    if (!hitCountType || hitCountType === this.defaultHitCountType) {
      return '';
    }
    const hitCountField_locKey = 'abbr_' + hitCountType;
    return this.$translate.instant(hitCountField_locKey);
  }

}

Module.instance().controller('ContentSubplaceCtrl', ContentSubplaceCtrl);
